/**
 * Facetra portfolio themes — 5 hand-picked presets that apply to the
 * shared portfolio view + ZIP export cover.
 *
 * Each theme owns a small design system: paper texture, ink colour,
 * accent, heading font family, and body font family. Applied via CSS
 * variables on the portfolio detail root so children pick up the theme
 * without prop drilling.
 */
export const PORTFOLIO_THEMES = [
  {
    key: "ivory",
    name: "Studio Ivory",
    tagline: "Warm off-white paper, graphite ink — mirrors the Kelly-style drafting pad.",
    swatches: ["#F3EEE3", "#3A3A38", "#B48A3D"],
    vars: {
      "--pf-bg": "#F3EEE3",
      "--pf-bg-accent": "#EAE1D0",
      "--pf-ink": "#2A2A28",
      "--pf-ink-soft": "#6B655A",
      "--pf-accent": "#B48A3D",
      "--pf-border": "rgba(42,42,40,0.16)",
      "--pf-card": "#FBF6EA",
    },
    fonts: { heading: "'Cormorant Garamond', serif", body: "'Inter', sans-serif" },
  },
  {
    key: "black-luxe",
    name: "Matte Black Luxe",
    tagline: "Facetra brand default — matte black with soft-gold accents.",
    swatches: ["#0A0A0A", "#F3F3F0", "#D4AF37"],
    vars: {
      "--pf-bg": "#0A0A0A",
      "--pf-bg-accent": "#141414",
      "--pf-ink": "#F3F3F0",
      "--pf-ink-soft": "rgba(243,243,240,0.65)",
      "--pf-accent": "#D4AF37",
      "--pf-border": "rgba(212,175,55,0.22)",
      "--pf-card": "#141414",
    },
    fonts: { heading: "'Cormorant Garamond', serif", body: "'Inter', sans-serif" },
  },
  {
    key: "museum",
    name: "Museum White",
    tagline: "Minimal gallery aesthetic — pure white, thin rules, editorial serif.",
    swatches: ["#FFFFFF", "#111111", "#8B7A5A"],
    vars: {
      "--pf-bg": "#FFFFFF",
      "--pf-bg-accent": "#F5F5F3",
      "--pf-ink": "#111111",
      "--pf-ink-soft": "#5C5C5C",
      "--pf-accent": "#8B7A5A",
      "--pf-border": "rgba(17,17,17,0.14)",
      "--pf-card": "#FAFAF8",
    },
    fonts: { heading: "'Playfair Display', serif", body: "'Inter', sans-serif" },
  },
  {
    key: "blueprint",
    name: "Blueprint Blue",
    tagline: "Technical drafting palette — indigo grid, white ink, engineering feel.",
    swatches: ["#0F2447", "#F3F3F0", "#F0C24E"],
    vars: {
      "--pf-bg": "#0F2447",
      "--pf-bg-accent": "#132C55",
      "--pf-ink": "#F3F3F0",
      "--pf-ink-soft": "rgba(243,243,240,0.7)",
      "--pf-accent": "#F0C24E",
      "--pf-border": "rgba(243,243,240,0.18)",
      "--pf-card": "#132C55",
    },
    fonts: { heading: "'IBM Plex Serif', serif", body: "'IBM Plex Sans', sans-serif" },
  },
  {
    key: "sepia",
    name: "Studio Sepia",
    tagline: "Heritage warm-brown palette — antique paper, oxidised-gold accents.",
    swatches: ["#EEE1CC", "#3A2A18", "#AA7A3C"],
    vars: {
      "--pf-bg": "#EEE1CC",
      "--pf-bg-accent": "#E4D3B3",
      "--pf-ink": "#3A2A18",
      "--pf-ink-soft": "#6C543A",
      "--pf-accent": "#AA7A3C",
      "--pf-border": "rgba(58,42,24,0.18)",
      "--pf-card": "#F6E9CE",
    },
    fonts: { heading: "'Cormorant Garamond', serif", body: "'Inter', sans-serif" },
  },
];

export const DEFAULT_THEME = "black-luxe";

export function themeByKey(key) {
  return PORTFOLIO_THEMES.find((t) => t.key === key) || PORTFOLIO_THEMES.find((t) => t.key === DEFAULT_THEME);
}

export function themeCssVars(key) {
  return themeByKey(key).vars;
}
